import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Music2, Youtube, Mail, ArrowRight } from "lucide-react";

const About = () => (
    <div data-testid="about-page" className="max-w-4xl mx-auto px-6 lg:px-10 pt-24 pb-32">
        <p className="font-script text-3xl text-hl-secondary hl-fade-up">two greeks, one kitchen —</p>
        <h1 className="hl-fade-up hl-delay-1 font-serif text-5xl md:text-7xl text-hl-brown mt-2 leading-[1.02]">
            More than <span className="italic">food.</span>
            <br />
            A story worth <span className="italic">preserving.</span>
        </h1>

        <div className="mt-12 space-y-7 text-hl-text text-xl leading-relaxed font-serif hl-fade-up hl-delay-2">
            <p className="hl-dropcap">
                iCookGreek was created from a simple idea: food is more than
                recipes. It is history, memory, family, and culture.
            </p>
            <p>
                We are two Greeks living abroad — one Cypriot, one Greek —
                <span className="text-hl-secondary font-semibold not-italic"> Haris and Michael</span>.
                When we met, we connected instantly through our love for our
                culture, our traditions, and the food we grew up with. Very
                quickly, that connection turned into a mission: to share the
                stories and the soul behind Greek and Cypriot cuisine with the
                world.
            </p>
            <p>
                What started as simple cooking videos slowly became something
                much bigger. People were connecting not only with the recipes,
                but with the history and emotions behind them. Some dishes came
                from villages, others from times of war, poverty, celebration,
                migration, and survival. Every recipe carried a story worth
                preserving.
            </p>
            <p className="italic text-hl-muted">
                Food is one of the strongest ways a culture survives through time.
            </p>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 gap-6">
            <div className="p-8 bg-hl-surface rounded-sm border border-hl-border hl-shadow-warm">
                <p className="text-[11px] uppercase tracking-[0.3em] text-hl-secondary">Our mission</p>
                <h3 className="font-serif text-2xl text-hl-brown mt-3 leading-tight">
                    Keep the traditions alive.
                </h3>
                <p className="text-hl-muted mt-3 text-sm leading-relaxed">
                    To preserve and spread the culture, history, and traditions
                    of Greek and Cypriot cuisine — and help a new generation
                    understand the meaning behind these dishes.
                </p>
            </div>
            <div className="p-8 bg-hl-surface rounded-sm border border-hl-border hl-shadow-warm">
                <p className="text-[11px] uppercase tracking-[0.3em] text-hl-secondary">This cookbook</p>
                <h3 className="font-serif text-2xl text-hl-brown mt-3 leading-tight">
                    A collection of memories.
                </h3>
                <p className="text-hl-muted mt-3 text-sm leading-relaxed">
                    Not just recipes — stories, traditions, and pieces of our
                    identity that we want to pass on to others.
                </p>
            </div>
        </div>

        <div className="mt-16">
            <p className="font-script text-3xl text-hl-secondary mb-4">say hello —</p>
            <div className="flex flex-wrap items-center gap-4">
                <a href="https://tiktok.com/@icookgreek" target="_blank" rel="noreferrer" data-testid="social-tiktok" className="inline-flex items-center gap-2 px-5 py-3 border border-hl-border rounded-full hover:border-hl-brown hl-btn-lift">
                    <Music2 className="w-4 h-4 text-hl-brown" strokeWidth={1.5} /> TikTok
                </a>
                <a href="https://www.instagram.com/icookmike/?hl=en" target="_blank" rel="noreferrer" data-testid="social-instagram" className="inline-flex items-center gap-2 px-5 py-3 border border-hl-border rounded-full hover:border-hl-primary hl-btn-lift">
                    <Instagram className="w-4 h-4 text-hl-primary" strokeWidth={1.5} /> Instagram
                </a>
                <a href="https://www.youtube.com/@Icookmike" target="_blank" rel="noreferrer" data-testid="social-youtube" className="inline-flex items-center gap-2 px-5 py-3 border border-hl-border rounded-full hover:border-hl-secondary hl-btn-lift">
                    <Youtube className="w-4 h-4 text-hl-secondary" strokeWidth={1.5} /> YouTube
                </a>
                <a href="mailto:greekicook@gmail.com" data-testid="social-email" className="inline-flex items-center gap-2 px-5 py-3 border border-hl-border rounded-full hover:border-hl-brown hl-btn-lift">
                    <Mail className="w-4 h-4 text-hl-brown" strokeWidth={1.5} /> Email
                </a>
            </div>

            <div className="mt-14 p-10 bg-hl-brown text-hl-bg rounded-sm hl-shadow-deep text-center">
                <p className="font-script text-3xl text-[#E8D9B5]">welcome to iCookGreek —</p>
                <p className="font-serif text-3xl md:text-4xl mt-3 italic leading-tight">
                    More than food. A story worth preserving.
                </p>
                <Link
                    to="/our-story"
                    className="inline-flex items-center gap-3 mt-8 text-[11px] uppercase tracking-[0.3em] text-[#E8D9B5] hl-link"
                >
                    Read our full story <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </Link>
            </div>
        </div>
    </div>
);

export default About;
